/**
 * TeamsPage Component
 * List all teams with search, filters, and server-side pagination
 */

import { useState, useEffect, useMemo } from 'react';
import { hasAdminAccess } from '../lib/roles';
import { useQuery, useMutation, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import { X, SlidersHorizontal } from 'lucide-react';
import { Layout } from '../components/layout/Layout';
import { TeamRow } from '../components/teams/TeamRow';
import { TeamFormModal } from '../components/teams/TeamFormModal';
import { DeleteConfirmModal } from '../components/ui/DeleteConfirmModal';
import { Input } from '../components/ui/Input';
import { Select } from '../components/ui/Select';
import { Button } from '../components/ui/Button';
import { BottomSheet } from '../components/ui/BottomSheet';
import { LoadingSpinner } from '../components/ui/LoadingSpinner';
import { Pagination } from '../components/ui/Pagination';
import { cn } from '../lib/utils';
import { teamsService } from '../services/teams.service';
import { membersService } from '../services/members.service';
import { departmentsService } from '../services/departments.service';
import { useAuth } from '../hooks/useAuth';
import { useToast } from '../hooks/useToast';
import { usePaginationParams } from '../hooks/usePaginationParams';

export const TeamsPage = () => {
  const { user } = useAuth();
  const toast = useToast();
  const queryClient = useQueryClient();

  // Pagination state from URL
  const { params, setPage, setLimit, setSearch, setFilter, clearFilters, correctPageIfNeeded } = usePaginationParams({
    defaultLimit: 25,
    defaultSort: 'name:asc',
    scrollToTop: true,
  });

  // Local search input state (for debouncing)
  const [searchInput, setSearchInput] = useState(params.search);

  // Mobile filter sheet
  const [filtersSheetOpen, setFiltersSheetOpen] = useState(false);
  const [deptSearch, setDeptSearch] = useState('');

  // Count active filters for the mobile Filters button badge
  const activeFilterCount = useMemo(() => (params.department_id ? 1 : 0), [params.department_id]);

  // Cleanup team image cache on unmount
  useEffect(() => {
    return () => {
      teamsService.clearTeamImageCache();
    };
  }, []);

  // Debounced search - update URL params after 300ms
  useEffect(() => {
    const timer = setTimeout(() => {
      if (searchInput !== params.search) {
        setSearch(searchInput);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [searchInput, params.search, setSearch]);

  // Modal states
  const [isTeamFormOpen, setIsTeamFormOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [teamFormMode, setTeamFormMode] = useState<'add' | 'edit'>('add');
  const [editingTeamId, setEditingTeamId] = useState<number | undefined>();
  const [deletingTeamId, setDeletingTeamId] = useState<number | undefined>();
  const [deletingTeamName, setDeletingTeamName] = useState('');

  // Fetch teams with server-side pagination
  const { data: teamsResponse, isLoading: teamsLoading, isFetching, error: teamsError, dataUpdatedAt } = useQuery({
    queryKey: ['teams', params],
    queryFn: () => teamsService.getPaginated({
      page: params.page,
      limit: params.limit,
      search: params.search || undefined,
      sort: params.sort,
      department_id: params.department_id ? Number(params.department_id) : undefined,
    }),
    placeholderData: keepPreviousData,
  });

  // Extract data and meta from response
  const teams = teamsResponse?.data ?? [];
  const meta = teamsResponse?.meta ?? { page: 1, limit: 25, total: 0, total_pages: 1 };

  // Correct page if it exceeds total pages
  useEffect(() => {
    if (teamsResponse?.meta) {
      correctPageIfNeeded(teamsResponse.meta.total_pages);
    }
  }, [teamsResponse?.meta, correctPageIfNeeded]);

  // Fetch members (needed for team lead display and member thumbnails)
  const { data: members = [], isLoading: membersLoading } = useQuery({
    queryKey: ['members'],
    queryFn: membersService.getAll,
  });

  // Fetch departments for filter dropdown
  const { data: departments = [] } = useQuery({
    queryKey: ['departments-all'],
    queryFn: departmentsService.getAll,
  });

  // Handle search input change
  const handleSearchInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchInput(e.target.value);
  };

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: (id: number) => teamsService.delete(id),
    onSuccess: () => {
      toast.success('Team deleted successfully');
      queryClient.invalidateQueries({ queryKey: ['teams'] });
      setIsDeleteModalOpen(false);
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Failed to delete team');
    },
  });

  // Modal handlers
  const handleAddTeam = () => {
    setTeamFormMode('add');
    setEditingTeamId(undefined);
    setIsTeamFormOpen(true);
  };

  const handleEditTeam = (teamId: number) => {
    setTeamFormMode('edit');
    setEditingTeamId(teamId);
    setIsTeamFormOpen(true);
  };

  const handleDeleteTeam = (teamId: number) => {
    const team = teams.find((t) => t.id === teamId);
    if (team) {
      setDeletingTeamId(teamId);
      setDeletingTeamName(team.name);
      setIsDeleteModalOpen(true);
    }
  };

  const confirmDelete = () => {
    if (deletingTeamId) {
      deleteMutation.mutate(deletingTeamId);
    }
  };

  const handleClearFilters = () => {
    setSearchInput('');
    clearFilters();
  };

  const isLoading = teamsLoading || membersLoading;

  // Check if any filters are active
  const hasActiveFilters = params.search || params.department_id;

  if (teamsError) {
    return (
      <Layout>
        <div className="text-center py-12">
          <svg className="mx-auto h-12 w-12 text-yellow-500 mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
          </svg>
          <h3 className="text-lg font-medium text-heading mb-2">Failed to load teams</h3>
          <p className="text-meta mb-4">There was an error loading the teams list.</p>
          <Button onClick={() => queryClient.invalidateQueries({ queryKey: ['teams'] })}>
            <svg className="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
            Try Again
          </Button>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-heading flex items-center">
              <svg className="h-8 w-8 text-blue-600 dark:text-blue-400 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
              </svg>
              Teams
            </h1>
          </div>
          {hasAdminAccess(user?.role) && (
            <Button onClick={handleAddTeam}>
              <svg className="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              Add Team
            </Button>
          )}
        </div>

        {/* Filters - Desktop (>= lg) */}
        <div className="hidden lg:block bg-surface rounded-lg shadow p-4">
          <div className="flex items-end gap-8">
            <div className="flex-1 max-w-[18%]">
              <label htmlFor="search-teams" className="block text-xs font-bold text-body mb-1">
                Search Teams
              </label>
              <Input
                id="search-teams"
                type="search"
                placeholder="Search by team name..."
                value={searchInput}
                onChange={handleSearchInputChange}
              />
            </div>
            <div className="flex-1 max-w-[22%]">
              <label htmlFor="department-filter" className="block text-xs font-bold text-body mb-1">
                Filter by Department
              </label>
              <Select
                id="department-filter"
                value={String(params.department_id || '')}
                onChange={(e) => setFilter('department_id', e.target.value || null)}
                options={[
                  { value: '', label: 'All Departments' },
                  ...departments.map((dept) => ({
                    value: String(dept.id),
                    label: dept.name,
                  })),
                ]}
              />
            </div>
            <div className="ml-auto">
              <Button
                variant="tertiary"
                onClick={handleClearFilters}
                className="flex items-center gap-2"
              >
                <X className="h-4 w-4 text-blue-600 dark:text-blue-400" />
                Clear Filters
              </Button>
            </div>
          </div>
        </div>

        {/* Filters - Mobile (< lg) */}
        <div className="lg:hidden bg-surface rounded-lg shadow p-4">
          <div className="flex flex-col sm:flex-row sm:items-end gap-3">
            <div className="flex-1 min-w-0">
              <label htmlFor="search-teams-mobile" className="block text-xs font-bold text-body mb-1">
                Search Teams
              </label>
              <Input
                id="search-teams-mobile"
                type="search"
                placeholder="Search by team name..."
                value={searchInput}
                onChange={handleSearchInputChange}
                className="text-base px-4 py-3 sm:text-sm sm:px-3 sm:py-2"
              />
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="tertiary"
                onClick={() => setFiltersSheetOpen(true)}
                className="flex items-center gap-2"
              >
                <SlidersHorizontal className="h-4 w-4 text-blue-600 dark:text-blue-400" />
                Filters{activeFilterCount > 0 ? ` · ${activeFilterCount}` : ''}
              </Button>
              <Button variant="tertiary" onClick={handleClearFilters} className="flex items-center gap-2">
                <X className="h-4 w-4 text-blue-600 dark:text-blue-400" />
                Clear Filters
              </Button>
            </div>
          </div>
        </div>

        {/* Mobile filters bottom sheet */}
        <BottomSheet
          isOpen={filtersSheetOpen}
          onClose={() => setFiltersSheetOpen(false)}
          title="Filters"
          footer={
            <div className="flex gap-6">
              <Button
                variant="secondary"
                onClick={handleClearFilters}
                className="flex-1 flex items-center justify-center gap-2"
              >
                <X className="h-4 w-4 text-blue-600 dark:text-blue-400" />
                Clear Filters
              </Button>
              <Button
                variant="primary"
                onClick={() => setFiltersSheetOpen(false)}
                className="flex-1"
              >
                Apply Filters
              </Button>
            </div>
          }
        >
          <div>
            <label className="block text-xs font-bold text-body mb-2">Filter by Department</label>
            <Input
              type="search"
              placeholder="Search departments..."
              value={deptSearch}
              onChange={(e) => setDeptSearch(e.target.value)}
              className="text-base px-4 py-3"
            />
            <div className="mt-2 max-h-56 overflow-y-auto border border-[var(--border-default)] rounded-md divide-y divide-[var(--border-default)]">
              <button
                type="button"
                onClick={() => setFilter('department_id', null)}
                className={cn(
                  'w-full text-left px-3 py-2 text-sm font-medium',
                  !params.department_id ? 'bg-blue-600 text-white' : 'text-body hover:bg-surface-hover'
                )}
              >
                All Departments
              </button>
              {departments
                .filter((d) => d.name.toLowerCase().includes(deptSearch.toLowerCase()))
                .map((dept) => {
                  const selected = String(params.department_id) === String(dept.id);
                  return (
                    <button
                      key={dept.id}
                      type="button"
                      onClick={() => setFilter('department_id', String(dept.id))}
                      className={cn(
                        'w-full text-left px-3 py-2 text-sm',
                        selected ? 'bg-blue-600 text-white font-medium' : 'text-body hover:bg-surface-hover'
                      )}
                    >
                      {dept.name}
                    </button>
                  );
                })}
            </div>
          </div>
        </BottomSheet>

        {/* Page Info */}
        {meta.total > 0 && (
          <div className="text-sm text-meta">
            Page {meta.page} of {meta.total_pages}{'\u00A0\u00A0'}•{'\u00A0\u00A0'}{meta.total} teams total
          </div>
        )}

        {/* Teams List */}
        <div className="relative">
          {/* Loading overlay */}
          {isFetching && !isLoading && (
            <div className="absolute inset-0 bg-surface/50 z-10 flex items-center justify-center">
              <LoadingSpinner size="md" />
            </div>
          )}

          {isLoading ? (
            <div className="flex justify-center py-12">
              <LoadingSpinner size="lg" text="Loading teams..." />
            </div>
          ) : teams.length === 0 ? (
            <div className="text-center py-12 bg-surface rounded-lg shadow">
              <svg className="mx-auto h-12 w-12 text-meta mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
              </svg>
              <h3 className="text-lg font-medium text-heading mb-2">
                {hasActiveFilters ? 'No teams found' : 'No teams yet'}
              </h3>
              <p className="text-meta mb-4">
                {hasActiveFilters ? 'Try adjusting your search.' : 'Get started by creating your first team.'}
              </p>
              {hasAdminAccess(user?.role) && !hasActiveFilters && (
                <Button onClick={handleAddTeam}>
                  <svg className="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                  </svg>
                  Create First Team
                </Button>
              )}
            </div>
          ) : (
            <div className="bg-surface rounded-lg shadow divide-y divide-[var(--border-default)]">
              {teams.map((team) => (
                <TeamRow
                  key={team.id}
                  team={team}
                  members={members}
                  onEdit={handleEditTeam}
                  onDelete={handleDeleteTeam}
                  refreshTrigger={dataUpdatedAt}
                />
              ))}
            </div>
          )}
        </div>

        {/* Pagination */}
        {meta.total > 0 && (
          <Pagination
            page={meta.page}
            limit={meta.limit}
            total={meta.total}
            totalPages={meta.total_pages}
            entityName="teams"
            onPageChange={setPage}
            onLimitChange={setLimit}
            isLoading={isFetching}
          />
        )}
      </div>

      {/* Modals */}
      <TeamFormModal
        isOpen={isTeamFormOpen}
        onClose={() => setIsTeamFormOpen(false)}
        mode={teamFormMode}
        teamId={editingTeamId}
      />
      <DeleteConfirmModal
        isOpen={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        onConfirm={confirmDelete}
        title="Delete Team"
        message="Are you sure you want to delete this team?"
        itemName={deletingTeamName}
        isDeleting={deleteMutation.isPending}
      />
    </Layout>
  );
};
